import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  UserIcon,
  CalendarDaysIcon,
  ClockIcon,
  MicrophoneIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { patientService } from '@/services/patient';
import type { ExternalSystemSlug } from '@/types/encounter';
import { appointmentWrapperService, AppointmentWithPatient } from '@/services/appointmentWrapper';
import { useAuth } from '@/hooks/useAuth';
import { EHR_PRE_LOCALIZED_SYSTEMS, formatEhrDateTime, formatDobWithAge, isSentinelDate } from '@/utils/dateFormat';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { CreateAppointmentData } from '@/types/appointment';
import { Patient as PatientType } from '@/types/patient';
import toast from 'react-hot-toast';
import { patientIdLabel } from '../utils/patientDisplay';
import { useOrganizationStore } from '../stores/organizationStore';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

interface Encounter {
  id: string;
  patientData: {
    firstName: string;
    lastName: string;
    dateOfBirth?: Date;
    mrn?: string;
  };
  encounterDetails: {
    scheduledDate: Date;
    type: string;
    status: string;
    chiefComplaint?: string;
  };
  matchStatus: 'matched' | 'unmatched' | 'pending';
  externalProviderId: string;
  externalSystemId: ExternalSystemSlug;
}

interface QuickRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedEncounter?: Encounter | null;
}

type RecordingMode = 'select-appointment' | 'create-instant' | 'select-patient' | 'select-encounter';

/** An upcoming appointment; patientId is null when its patient could not be loaded. */
type UpcomingAppointment = Omit<AppointmentWithPatient, 'patientId'> & { patientId: PatientType | null };

const UNKNOWN_PATIENT = 'Unknown patient';

/**
 * AMP-136 refusal, on the shared Dialog: focus moves in, Tab stays inside and Escape closes it.
 * Radix only returns focus to a Dialog.Trigger and this modal has none, so the element that had
 * focus when the refusal opened is remembered and focused again on close.
 */
const RecordingRefusalDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const returnFocusTo = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
  );
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        role="alertdialog"
        className="sm:max-w-md text-center"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusTo.current?.focus();
        }}
      >
        <DialogHeader className="sm:text-center">
          <DialogTitle>Recording is for providers</DialogTitle>
          <DialogDescription className="text-gray-600">
            In this practice, visits are recorded by providers. Ask the patient&apos;s provider to record the visit.
          </DialogDescription>
        </DialogHeader>
        <div>
          <button type="button" className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QuickRecordingModal: React.FC<QuickRecordingModalProps> = ({ isOpen, onClose, preselectedEncounter }) => {
  const { operationMode, canRecord } = useOrganizationStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { personalUsage, fetchPersonalUsage } = useSubscriptionStore();

  // Check if we're in encounter mode (URL has encounterId)
  const encounterId = searchParams.get('encounterId');

  // Initialize mode based on preselectedEncounter
  const [mode, setMode] = useState<RecordingMode>(
    preselectedEncounter ? 'select-encounter' : 'select-appointment'
  );
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Overage confirmation state
  const [showOverageConfirm, setShowOverageConfirm] = useState(false);
  const [pendingRecordingAction, setPendingRecordingAction] = useState<(() => void) | null>(null);

  // Appointment selection state
  const [appointments, setAppointments] = useState<UpcomingAppointment[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentWithPatient | null>(null);

  // Patient selection state
  const [patients, setPatients] = useState<PatientType[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientType | null>(null);

  // Encounter selection state (connected mode)
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(preselectedEncounter || null);

  // Instant appointment state
  const [instantAppointment, setInstantAppointment] = useState({
    type: 'consultation' as 'consultation' | 'follow-up' | 'procedure' | 'emergency' | 'other',
    location: '',
    chiefComplaint: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchPersonalUsage();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mode === 'select-appointment') {
      loadUpcomingAppointments();
    }
  }, [isOpen, mode]);

  useEffect(() => {
    if (isOpen && mode === 'select-patient') {
      loadPatients();
    }
  }, [isOpen, mode]); // loadPatients depends on searchTerm which triggers separately

  // Don't render modal if we're in encounter mode (URL has encounterId)
  // This check must be AFTER all hooks to comply with Rules of Hooks
  if (encounterId) {
    return null;
  }

  const loadUpcomingAppointments = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      
      const response = await appointmentWrapperService.getAppointments({
        page: 1,
        limit: 50,
        dateFrom: today.toISOString(),
        dateTo: nextWeek.toISOString()
      });
      
      // Need to fetch patient details for each appointment. When that fails the appointment is
      // still listed, as "Unknown patient", with no invented name or date of birth.
      const appointmentsWithPatients: UpcomingAppointment[] = [];
      
      for (const apt of response.appointments) {
        let patient: PatientType | null = null;
        try {
          // Extract patient ID properly (handle both string and object)
          const patientId = typeof apt.patientId === 'string' 
            ? apt.patientId 
            : (apt.patientId as any).id || (apt.patientId as any).toString();
          patient = await appointmentWrapperService.getPatient(patientId);
        } catch (error) {
          console.warn('Failed to load the patient for an upcoming appointment', { appointmentId: apt.id, error });
        }
        appointmentsWithPatients.push({
          id: apt.id,
          patientId: patient,
          appointmentDetails: apt.appointmentDetails,
          recording: apt.recording
        });
      }
      
      setAppointments(appointmentsWithPatients);
    } catch (error) {
      console.error('Failed to load appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    setLoading(true);
    try {
      // Active patients only: the server refuses to book an archived one.
      const response = await patientService.getPatients({
        page: 1,
        limit: 100,
        search: searchTerm,
        isActive: true
      });
      
      setPatients(response.patients.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if the user is in overage territory and needs confirmation.
   * Returns true if the action should proceed immediately, false if confirmation is pending.
   */
  const checkOverageBeforeRecording = (proceedAction: () => void): boolean => {
    if (
      personalUsage &&
      personalUsage.myUsage.personalEncounterLimit > 0 &&
      personalUsage.myUsage.isOverPersonalLimit &&
      personalUsage.myUsage.overagesAllowed
    ) {
      setPendingRecordingAction(() => proceedAction);
      setShowOverageConfirm(true);
      return false;
    }
    return true;
  };

  const handleStartRecording = async (appointmentId: string) => {
    const proceed = () => {
      onClose();
      navigate(`/recording/${appointmentId}`);
    };
    if (checkOverageBeforeRecording(proceed)) {
      proceed();
    }
  };

  const doCreateInstantAppointment = async () => {
    if (!selectedPatient || !instantAppointment.location.trim()) {
      toast.error('Please select a patient and provide a location');
      return;
    }

    setLoading(true);
    try {
      const appointmentData: CreateAppointmentData = {
        patientId: selectedPatient.id,
        providerId: user?.id,
        appointmentDetails: {
          scheduledDate: new Date().toISOString(),
          type: instantAppointment.type,
          location: instantAppointment.location,
          chiefComplaint: instantAppointment.chiefComplaint
        }
      };

      const appointment = await appointmentWrapperService.createAppointment(appointmentData);

      toast.success('Instant appointment created!');
      onClose();
      navigate(`/recording/${appointment.id}`);
    } catch (error) {
      console.error('Failed to create appointment:', error);
      toast.error('Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInstantAppointment = async () => {
    if (!selectedPatient || !instantAppointment.location.trim()) {
      toast.error('Please select a patient and provide a location');
      return;
    }
    if (!checkOverageBeforeRecording(doCreateInstantAppointment)) return;
    doCreateInstantAppointment();
  };

  const handleStartRecordingFromEncounter = async () => {
    if (!selectedEncounter) {
      toast.error('Please select an encounter');
      return;
    }

    const proceed = () => {
      onClose();
      navigate(`/recording?encounterId=${selectedEncounter.id}`);
    };
    if (checkOverageBeforeRecording(proceed)) {
      proceed();
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    if (!searchTerm) return true;
    const patient = apt.patientId;
    const fullName = patient ? `${patient.firstName} ${patient.lastName}`.toLowerCase() : UNKNOWN_PATIENT.toLowerCase();
    const patientId = patient?.patientId?.toLowerCase() || '';
    const mrn = patient?.medicalRecord?.mrn?.toLowerCase() || '';
    const complaint = apt.appointmentDetails.chiefComplaint?.toLowerCase() || '';
    
    return fullName.includes(searchTerm.toLowerCase()) ||
           patientId.includes(searchTerm.toLowerCase()) ||
           (!!mrn && mrn.includes(searchTerm.toLowerCase())) ||
           complaint.includes(searchTerm.toLowerCase());
  });

  const filteredPatients = patients.filter(patient => {
    if (!searchTerm) return true;
    const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
    const patientId = patient.patientId?.toLowerCase() || '';
    const mrn = patient.medicalRecord?.mrn?.toLowerCase() || '';
    
    return fullName.includes(searchTerm.toLowerCase()) ||
           patientId.includes(searchTerm.toLowerCase()) ||
           (!!mrn && mrn.includes(searchTerm.toLowerCase()));
  });

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getAppointmentTypeColor = (type: string) => {
    const colors = {
      consultation: 'bg-blue-100 text-blue-800',
      'follow-up': 'bg-green-100 text-green-800',
      procedure: 'bg-purple-100 text-purple-800',
      emergency: 'bg-red-100 text-red-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[type as keyof typeof colors] || colors.other;
  };

  if (!isOpen) return null;

  // AMP-136: in a standalone practice, providers record; org admins do not.
  if (canRecord === false) {
    return (
      <RecordingRefusalDialog onClose={onClose} />
    );
  }

  // Overage confirmation dialog
  if (showOverageConfirm && personalUsage) {
    const overagePrice = personalUsage.myUsage.overagePrice;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 bg-amber-100 p-2 rounded-full">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Overage Encounter</h3>
              <p className="mt-2 text-sm text-gray-600">
                You've exceeded your monthly encounter limit of{' '}
                <span className="font-medium">{personalUsage.myUsage.personalEncounterLimit}</span> encounters.
                Extra encounters are charged at{' '}
                <span className="font-medium">${overagePrice.toFixed(2)}</span> each.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Current overages: {personalUsage.myUsage.overageEncounters} encounter{personalUsage.myUsage.overageEncounters !== 1 ? 's' : ''}{' '}
                (${(personalUsage.myUsage.overageEncounters * overagePrice).toFixed(2)})
              </p>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => {
                setShowOverageConfirm(false);
                setPendingRecordingAction(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowOverageConfirm(false);
                if (pendingRecordingAction) {
                  pendingRecordingAction();
                  setPendingRecordingAction(null);
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
            >
              Continue Recording
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200 px-6 pt-6 pb-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100">
                <MicrophoneIcon className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Start Recording</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Pick a scheduled appointment, or create an instant one for a walk-in.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mode Selection (segmented control) */}
          <div className="mt-5 inline-flex rounded-xl bg-white/70 border border-gray-200 p-1">
            <button
              onClick={() => setMode('select-appointment')}
              aria-pressed={mode === 'select-appointment'}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                mode === 'select-appointment'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarDaysIcon className="w-4 h-4" />
              Select Appointment
            </button>
            <button
              onClick={() => setMode('create-instant')}
              aria-pressed={mode === 'create-instant'}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                mode === 'create-instant'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <PlusIcon className="w-4 h-4" />
              Create Instant
            </button>
          </div>
        </div>

        <div className="flex-1 px-6 py-5 overflow-y-auto">
          {mode === 'select-appointment' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={`Search appointments by patient name, ${operationMode === 'standalone' ? 'MRN' : 'ID'}, or complaint...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Appointments List */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading appointments...</p>
                </div>
              ) : filteredAppointments.length > 0 ? (
                <div className="space-y-3">
                  {filteredAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors cursor-pointer"
                      onClick={() => handleStartRecording(appointment.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <UserIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {appointment.patientId
                                  ? `${appointment.patientId.firstName} ${appointment.patientId.lastName}`
                                  : UNKNOWN_PATIENT}
                              </h3>
                              {appointment.patientId && (
                                <p className="text-sm text-gray-500">
                                  {patientIdLabel(appointment.patientId, operationMode)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <ClockIcon className="w-4 h-4 mr-1" />
                              {formatDateTime(appointment.appointmentDetails.scheduledDate)}
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAppointmentTypeColor(appointment.appointmentDetails.type)}`}>
                              {appointment.appointmentDetails.type}
                            </span>
                            <span>{appointment.appointmentDetails.location}</span>
                          </div>
                          {appointment.appointmentDetails.chiefComplaint && (
                            <p className="mt-1 text-sm text-gray-600">
                              <strong>Chief Complaint:</strong> {appointment.appointmentDetails.chiefComplaint}
                            </p>
                          )}
                        </div>
                        <MicrophoneIcon className="w-6 h-6 text-indigo-600" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
                    <CalendarDaysIcon className="w-7 h-7 text-indigo-400" />
                  </div>
                  <p className="mt-4 font-medium text-gray-900">No upcoming appointments</p>
                  <p className="mt-1 text-sm text-gray-500">
                    Try a different search, or start an instant recording for a walk-in visit.
                  </p>
                  <button
                    onClick={() => setMode('create-instant')}
                    className="mt-5 btn-primary inline-flex items-center gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Create Instant Appointment
                  </button>
                </div>
              )}
            </div>
          )}

          {mode === 'create-instant' && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100">
                  <PlusIcon className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-medium text-indigo-900">Instant appointment</h3>
                  <p className="text-sm text-indigo-700 mt-0.5">
                    Scheduled for the current time so you can start recording right away.
                  </p>
                </div>
              </div>

              {/* Patient Selection */}
              {!selectedPatient ? (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Select Patient</h4>
                  
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search patients by name or ID..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        if (e.target.value) {
                          loadPatients();
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  {searchTerm && (
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                      {loading ? (
                        <div className="p-4 text-center">Loading patients...</div>
                      ) : filteredPatients.length > 0 ? (
                        filteredPatients.map((patient) => (
                          <div
                            key={patient.id}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => {
                              setSelectedPatient(patient);
                              setSearchTerm('');
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-medium text-gray-900">
                                  {patient.firstName} {patient.lastName}
                                </h3>
                                <p className="text-sm text-gray-500">
                                  {patientIdLabel(patient, operationMode)} • {patient.demographics.gender} • 
                                  Born: {formatDobWithAge(patient.demographics.dateOfBirth) ?? 'Unknown'}
                                </p>
                              </div>
                              <UserIcon className="w-5 h-5 text-gray-400" />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500">No patients found</div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Selected Patient</h4>
                    <button
                      onClick={() => setSelectedPatient(null)}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      Change Patient
                    </button>
                  </div>
                  
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h3 className="font-medium text-gray-900">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {patientIdLabel(selectedPatient, operationMode)} • {selectedPatient.demographics.gender} • 
                      Born: {formatDobWithAge(selectedPatient.demographics.dateOfBirth) ?? 'Unknown'}
                    </p>
                  </div>

                  {/* Appointment Details */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Appointment Details</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Appointment Type *
                        </label>
                        <select
                          value={instantAppointment.type}
                          onChange={(e) => setInstantAppointment(prev => ({ 
                            ...prev, 
                            type: e.target.value as any 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="consultation">Consultation</option>
                          <option value="follow-up">Follow-up</option>
                          <option value="procedure">Procedure</option>
                          <option value="emergency">Emergency</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location *
                        </label>
                        <input
                          type="text"
                          value={instantAppointment.location}
                          onChange={(e) => setInstantAppointment(prev => ({ 
                            ...prev, 
                            location: e.target.value 
                          }))}
                          placeholder="Room 101, Office, etc."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Chief Complaint
                      </label>
                      <textarea
                        value={instantAppointment.chiefComplaint}
                        onChange={(e) => setInstantAppointment(prev => ({ 
                          ...prev, 
                          chiefComplaint: e.target.value 
                        }))}
                        placeholder="Brief description of the visit reason..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === 'select-encounter' && selectedEncounter && (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h3 className="font-medium text-indigo-900 mb-2">Connected Mode - Encounter Recording</h3>
                <p className="text-sm text-indigo-700">
                  You're about to start a recording for this encounter. Patient and provider data will be retrieved from the encounter.
                </p>
              </div>

              {/* Encounter Confirmation */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Patient</label>
                    <p className="text-base text-gray-900">
                      {selectedEncounter.patientData.firstName} {selectedEncounter.patientData.lastName}
                    </p>
                    {selectedEncounter.patientData.mrn && (
                      <p className="text-sm text-gray-500">MRN: {selectedEncounter.patientData.mrn}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      DOB: {!selectedEncounter.patientData.dateOfBirth || isSentinelDate(selectedEncounter.patientData.dateOfBirth)
                        ? 'Not Available'
                        : new Date(selectedEncounter.patientData.dateOfBirth).toLocaleDateString()}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Encounter Details</label>
                    <p className="text-sm text-gray-600">
                      Type: {selectedEncounter.encounterDetails.type}
                    </p>
                    <p className="text-sm text-gray-600">
                      Date: {EHR_PRE_LOCALIZED_SYSTEMS.has(selectedEncounter.externalSystemId)
                        ? formatEhrDateTime(selectedEncounter.encounterDetails.scheduledDate)
                        : new Date(selectedEncounter.encounterDetails.scheduledDate).toLocaleString()}
                    </p>
                    {selectedEncounter.encounterDetails.chiefComplaint && (
                      <p className="text-sm text-gray-600">
                        Chief Complaint: {selectedEncounter.encounterDetails.chiefComplaint}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Provider</label>
                    <p className="text-sm text-gray-600">{selectedEncounter.externalProviderId}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Match Status</label>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      selectedEncounter.matchStatus === 'matched'
                        ? 'bg-green-100 text-green-800'
                        : selectedEncounter.matchStatus === 'unmatched'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedEncounter.matchStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>

            {mode === 'create-instant' && selectedPatient && (
              <button
                onClick={handleCreateInstantAppointment}
                disabled={loading || !instantAppointment.location.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <MicrophoneIcon className="w-4 h-4" />
                )}
                <span>Create & Start Recording</span>
              </button>
            )}

            {mode === 'select-encounter' && selectedEncounter && (
              <button
                onClick={handleStartRecordingFromEncounter}
                disabled={loading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <MicrophoneIcon className="w-4 h-4" />
                )}
                <span>Start Recording</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickRecordingModal;